import { Button, SkeletonLoading } from '@saleshandy/design-system';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { Diamond, Download } from '@saleshandy/icons';
import classNames from 'classnames';
import PreSelect from '../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/select-file/components/pre-select';
import { Stage } from '../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/import-flow/stage';

import { accessibleOnClick } from '../../../shared/utils/accessible-on-click';
import { FileData } from '../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/select-file/types';
import { ProductTour } from '../../../shared/enums/product-tour';
import { constants } from '../../../shared/enums/constants';
import FileUpload from '../file-upload-module';
import { RequestStatus } from '../../../shared/enums/request-status';
import { FileSelectAndDropFileProps } from './type';
import PostSelect from '../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/select-file/components/post-select/post-select';
import { Theme } from '../../../shared/design-system/components/overlay';
import toaster from '../../../shared/toaster';
import { truncate } from '../../../shared/utils';

/**
 *
 * @param currentPlanName - Gives name of the current plan.
 * @param getEmailVerificationPlanResponse - Function to get email verification plan response.
 * FileUploadModule is a React component that handles the file upload process for email verification.
 * It allows users to select a CSV file, map fields, and verify emails while managing stages of the import process.
 * @returns React Component
 */

const FileSelectAndDropFile: React.FC<FileSelectAndDropFileProps> = memo(
  ({
    currentPlanName,
    getEmailVerificationPlanResponse,
    sendPurchaseEmailVerificationCreditsRequestStatus,
    sendGetEmailVerificationCreditsRequest,
    getPurchaseEmailVerificationCreditsResponse,
    sendPurchaseEmailVerificationCreditsRequestMessage,
    emailVerifyCredits,
  }) => {
    const [fileData, setFileData] = useState<FileData>(null);
    const [stage, setStage] = useState<Stage>(Stage.SelectFile);
    const [csvError, setCsvError] = useState<{
      error: boolean;
      message_one: string;
      message_two: string;
    }>({
      error: false,
      message_one: '',
      message_two: '',
    });

    const [showFileUploadModal, setShowFileUploadModal] = useState<boolean>(
      false,
    );
    const [
      showPurchaseCreditModel,
      setShowPurchaseCreditModel,
    ] = useState<boolean>(false);

    const onRemoveHandler = useCallback(() => {
      setFileData(null);
      if (csvError) {
        setCsvError({
          error: false,
          message_one: '',
          message_two: '',
        });
      }
    }, [csvError]);

    const hasMoreThanTwoEmptyStrings = useCallback((arr: string[]) => {
      const emptyStringsCount = arr?.filter((item) => item === '')?.length;
      return emptyStringsCount >= 2;
    }, []);

    const hasMoreThanTwoIdenticalNonEmptyStrings = useCallback(
      (arr: string[]): boolean => {
        if (!arr) {
          return false;
        }

        const uniqueValues = new Set<string>();

        const mappedArr = arr?.map((item) => item?.toLowerCase());

        // eslint-disable-next-line no-restricted-syntax
        for (const item of mappedArr) {
          if (item !== '') {
            if (uniqueValues?.has(item)) {
              return true;
            }
            uniqueValues?.add(item);
          }
        }
        return false;
      },
      [],
    );

    const onSelectSuccess = useCallback(
      (file, headers, firstRow, count) => {
        const payload = {
          file,
          headers,
          firstRow,
          importedContactsCount: count - 1,
        };
        setFileData(payload);

        const hasEmptyValues = hasMoreThanTwoEmptyStrings(headers);
        const hasDuplicateValues = hasMoreThanTwoIdenticalNonEmptyStrings(
          headers,
        );

        if (hasEmptyValues && hasDuplicateValues) {
          setCsvError({
            error: true,
            message_one:
              'The uploaded file has duplicate column headers and blank columns between data columns.',
            message_two: 'Please fix this and re-upload the file.',
          });
        } else if (hasEmptyValues) {
          setCsvError({
            error: true,
            message_one:
              'The uploaded file has multiple blank columns between data columns.',
            message_two: 'Please remove them and re-upload the file.',
          });
        } else if (hasDuplicateValues) {
          setCsvError({
            error: true,
            message_one: 'The uploaded file has duplicate column headers.',
            message_two:
              'Please ensure the column header is unique and re-upload the file.',
          });
        }
        if (fileData?.importedContactsCount > 10000) {
          toaster.error('Maximum 100000 prospects are allowed.');
          setFileData(null);
          return;
        }
        if (hasEmptyValues || hasDuplicateValues) {
          toaster.error('Import prospect CSV failed');
        } else {
          toaster.success(
            `${truncate(
              file?.name || '',
              30,
              false,
            )} file selected successfully`,
            {
              theme: Theme.New,
            },
          );
        }
      },
      [
        setFileData,
        setCsvError,
        hasMoreThanTwoEmptyStrings,
        hasMoreThanTwoIdenticalNonEmptyStrings,
      ],
    );

    const onNextClick = useCallback(() => {
      setStage(Stage.MapFields);
      setShowFileUploadModal(true);
    }, []);

    const onCreditsClick = useCallback(() => {
      setShowPurchaseCreditModel(true);
    }, []);

    const dragDropModuleClassName = useMemo(
      () =>
        classNames('drag-drop-module', {
          'drag-drop-module-select': !!fileData,
          'drag-drop-module-error': csvError.error,
        }),
      [fileData, csvError?.error],
    );

    return (
      <>
        <div className="drag-drop-title">
          {sendGetEmailVerificationCreditsRequest === RequestStatus.Pending ? (
            <SkeletonLoading width={141} height={16} />
          ) : (
            <div className="email-credits">
              <span {...accessibleOnClick(() => onCreditsClick())}>
                <Diamond className="email-diamond-icon" />
                {`${emailVerifyCredits?.creditsAvailable} Credits`}
              </span>
            </div>
          )}
        </div>
        <div className={dragDropModuleClassName}>
          {!fileData ? (
            <PreSelect
              onSelectSuccess={onSelectSuccess}
              customTitle="or Drag & Drop your CSV with Prospect Details"
              showOr={false}
              customClassName="drag-drop-module-pre-select"
            />
          ) : (
            <PostSelect
              fileName={fileData?.file?.name}
              onRemove={onRemoveHandler}
              csvError={csvError}
            />
          )}
          <FileUpload
            fileData={fileData}
            setFileData={setFileData}
            stage={stage}
            setStage={setStage}
            showModal={showFileUploadModal}
            setShowModal={setShowFileUploadModal}
            currentPlanName={currentPlanName}
            getEmailVerificationPlanResponse={getEmailVerificationPlanResponse}
            showPurchaseCreditModel={showPurchaseCreditModel}
            setShowPurchaseCreditModel={setShowPurchaseCreditModel}
            sendPurchaseEmailVerificationCreditsRequestStatus={
              sendPurchaseEmailVerificationCreditsRequestStatus
            }
            sendGetEmailVerificationCreditsRequest={
              sendGetEmailVerificationCreditsRequest
            }
            getPurchaseEmailVerificationCreditsResponse={
              getPurchaseEmailVerificationCreditsResponse
            }
            sendPurchaseEmailVerificationCreditsRequestMessage={
              sendPurchaseEmailVerificationCreditsRequestMessage
            }
          />
        </div>
        {/* )} */}
        <div className="file-upload-footer">
          <span className="file-upload-sample-file-container">
            <a
              id={ProductTour.DownloadSampleCSV}
              href={constants.DOWNLOAD_EMAIL_VERIFIER_SAMPLE_FILE_URL}
              download
            >
              <Download width={20} height={20} />
              <span className="file-upload-sample-file-download">
                Try sample file
              </span>
            </a>
          </span>
          <div className="file-upload-btn">
            <Button
              className="next-btn"
              {...accessibleOnClick(() => onNextClick())}
              disabled={fileData === null || csvError?.error}
            >
              Next
            </Button>
          </div>
        </div>
      </>
    );
  },
);
export default FileSelectAndDropFile;
